import { useState } from 'react'
import { Button, DatePicker, Form, Select, Tag, App } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { shiftRostersApi, employeesApi } from '@/features/hr/api'
import type { ShiftRoster } from '@/shared/types/hr'
import type { Shift } from '@/shared/types/master'
import DataTable from '@/shared/components/DataTable'
import FormDrawer from '@/shared/components/FormDrawer'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'
import apiClient from '@/shared/api/client'
import dayjs from 'dayjs'

const STATUS_COLORS: Record<string, string> = { PLANNED: 'blue', CONFIRMED: 'green', CHANGED: 'orange' }

export default function ShiftRosterPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [form] = Form.useForm()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [empFilter, setEmpFilter] = useState<string | undefined>()
  const [dateRange, setDateRange] = useState<[string, string] | undefined>()
  const [drawerOpen, setDrawerOpen] = useState(false)

  const canCreate = usePermission('roster.create')

  const { data, isLoading } = useQuery({
    queryKey: ['rosters', page, pageSize, empFilter, dateRange],
    queryFn: () => shiftRostersApi.list({
      page, size: pageSize,
      employee_id: empFilter,
      date_from: dateRange?.[0],
      date_to: dateRange?.[1],
    }),
  })

  const { data: employeesData } = useQuery({
    queryKey: ['employees-all'],
    queryFn: () => employeesApi.list({ page: 1, size: 100, is_active: true }),
  })

  const { data: shiftsData } = useQuery({
    queryKey: ['shifts-all'],
    queryFn: async () => {
      // GET /shifts returns a plain Shift[] array (NOT a paginated
      // { items: [...], total } envelope). Older code had this wrong
      // and the dropdown silently showed "No data" — fixed here.
      const res = await apiClient.get<Shift[]>('/shifts', { params: { is_active: true } })
      return res.data
    },
  })

  const empOptions = employeesData?.items.map((e) => ({ label: `${e.emp_code} — ${e.emp_name}`, value: e.id })) ?? []
  const shiftOptions = (shiftsData ?? []).map((s) => ({
    label: `${s.shift_code} — ${s.shift_name} (${s.start_time}–${s.end_time})`,
    value: s.id,
  }))

  const openCreate = () => {
    form.resetFields()
    setDrawerOpen(true)
  }

  const bulkMutation = useMutation({
    mutationFn: (values: Record<string, unknown>) => {
      const range = values.date_range as [dayjs.Dayjs, dayjs.Dayjs]
      return shiftRostersApi.bulkCreate({
        employee_ids: values.employee_ids,
        shift_id: values.shift_id,
        date_from: range[0].format('YYYY-MM-DD'),
        date_to: range[1].format('YYYY-MM-DD'),
      })
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['rosters'] })
      message.success(res.message)
      setDrawerOpen(false)
    },
    onError: () => message.error('Save failed'),
  })

  const onSubmit = () => form.validateFields().then((v) => bulkMutation.mutate(v))

  const columns = [
    { title: 'Employee', dataIndex: 'employee_name', key: 'employee_name' },
    { title: 'Shift', dataIndex: 'shift_name', key: 'shift_name' },
    { title: 'Date', dataIndex: 'roster_date', key: 'roster_date', width: 120 },
    {
      title: 'Status', dataIndex: 'roster_status', key: 'roster_status', width: 110,
      render: (v: string) => <Tag color={STATUS_COLORS[v] ?? 'default'}>{v}</Tag>,
    },
  ]

  return (
    <div>
      <PageHeader
        title="Shift Roster"
        subtitle="Assign shifts to employees"
        extra={canCreate && <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Assign Shifts</Button>}
      />

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <Select allowClear showSearch optionFilterProp="label" placeholder="Filter by employee" options={empOptions} onChange={(v) => { setEmpFilter(v); setPage(1) }} style={{ width: 280 }} />
        <DatePicker.RangePicker onChange={(_, ds) => { setDateRange(ds[0] ? [ds[0], ds[1]] : undefined); setPage(1) }} />
      </div>

      <DataTable rowKey="id" columns={columns} dataSource={data?.items} loading={isLoading} total={data?.total} page={page} pageSize={pageSize} onPageChange={(p, ps) => { setPage(p); setPageSize(ps) }} />

      <FormDrawer title="Assign Shifts (Bulk)" open={drawerOpen} onClose={() => setDrawerOpen(false)} onSubmit={onSubmit} loading={bulkMutation.isPending} form={form}>
        <Form.Item name="employee_ids" label="Employees" rules={[{ required: true }]}>
          <Select mode="multiple" showSearch optionFilterProp="label" options={empOptions} placeholder="Select employees" />
        </Form.Item>
        <Form.Item name="shift_id" label="Shift" rules={[{ required: true }]}><Select options={shiftOptions} placeholder="Select shift" /></Form.Item>
        <Form.Item name="date_range" label="Date Range" rules={[{ required: true }]}><DatePicker.RangePicker style={{ width: '100%' }} /></Form.Item>
      </FormDrawer>
    </div>
  )
}
